import { FormProvider, useForm } from "react-hook-form";
import { FormModal, FormSectionLabel, Input, ResponsiveGrid } from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { BillingDetailsFields, emptyBillingDetails } from "../../components/BillingDetailsFields";
import {
  useCreateCompany,
  useUpdateCompany,
  type Company,
  type CompanyInput,
} from "../../lib/queries/companies";

interface CompanyFormValues {
  billing_details: ReturnType<typeof emptyBillingDetails>;
  default_vat_rate: string;
  default_due_days: string;
}

function toValues(c?: Company): CompanyFormValues {
  const bd = emptyBillingDetails();
  if (c) {
    for (const k of Object.keys(bd) as (keyof typeof bd)[]) {
      (bd as Record<string, unknown>)[k] = c.billing_details[k] ?? bd[k];
    }
  }
  return {
    billing_details: bd,
    default_vat_rate: String(c?.default_vat_rate ?? 21),
    default_due_days: String(c?.default_due_days ?? 14),
  };
}

/**
 * Create a company (admin) or edit one's billing details and defaults
 * (manager). A change of billing details becomes a new snapshot on the
 * server; documents keep the one they were issued with.
 */
export function CompanyFormModal({ company, onClose }: { company?: Company; onClose: () => void }) {
  const create = useCreateCompany();
  const update = useUpdateCompany(company?.id ?? "");
  const form = useForm<CompanyFormValues>({ mode: "onTouched", defaultValues: toValues(company) });
  const { register, handleSubmit, setError, formState } = form;
  const busy = create.isPending || update.isPending;

  async function onSubmit(v: CompanyFormValues) {
    const input: CompanyInput = {
      billing_details: v.billing_details,
      default_vat_rate: Number(v.default_vat_rate),
      default_due_days: Number(v.default_due_days),
    };
    try {
      if (company) {
        await update.mutateAsync(input);
        notifySuccess("Údaje firmy byly uloženy.");
      } else {
        await create.mutateAsync(input);
        notifySuccess("Firma byla založena.");
      }
      onClose();
    } catch (err) {
      apiErrorToFormErrors<CompanyFormValues>(err, setError, { fallbackMessage: "Uložení se nepovedlo." });
    }
  }

  return (
    <FormModal
      open
      title={company ? "Údaje firmy" : "Nová firma"}
      width={720}
      busy={busy}
      error={rootErrorMessage(formState.errors)}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
      submitLabel={company ? "Uložit" : "Založit"}
    >
      <FormProvider {...form}>
          <BillingDetailsFields />
          <FormSectionLabel>Výchozí hodnoty dokladů</FormSectionLabel>
          <ResponsiveGrid>
            <Input
              label="Sazba DPH (%)"
              type="number"
              min={0}
              max={100}
              {...register("default_vat_rate", { required: "Povinné pole" })}
              error={formState.errors.default_vat_rate?.message}
              hint="Pro neplátce 0."
            />
            <Input
              label="Splatnost (dní)"
              type="number"
              min={0}
              max={365}
              {...register("default_due_days", { required: "Povinné pole" })}
              error={formState.errors.default_due_days?.message}
            />
          </ResponsiveGrid>
      </FormProvider>
    </FormModal>
  );
}
